import { useEffect, useState } from "react";
import { Modal } from "../Modal";
import currencyIcon from "../../../icons/currency-small.png";
import styles from "./ModalBonus.module.scss";
import { formatNumberWithSpaces } from "../../../utils/numbers";
import { useDispatch, useSelector } from "../../../hooks";
import { applyBonuses } from "../../../redux/user/apply-bonuses.handler";
import { BonusReason, BonusType, IUserBonus } from "../../../model/user-info.interface";
import { ErrorSnackbar } from "../../ErrorSnackbar/ErrorSnackbar";
import { Loader } from "../../Loader/Loader";
import bonusIcon from "./icons/bonus.png";
import { Ticket, TicketType } from "../../TicketsPanel/Ticket";
import { omit } from "lodash";
import { setUserBalance, setUserTickets } from "../../../redux/user";
import { ThinButton } from "../../Buttons/ThinButton/ThinButton";
import { trueFragmentsForm } from "../../../utils/true.word.form";
import { setFragments } from "../../../redux/draws";
import { reachYMGoal, YMGoals } from "../../App/YandexMetrika";
import { CustomNotifyRouter, CustomNotifyType } from "../ModalNotify/Custom/CustomNotifyRouter";

const convertBonusToTicketType = (bonusType: BonusType): TicketType | undefined => {
    switch (bonusType) {
        case BonusType.RegularTicket: return TicketType.Regular;
        case BonusType.RareTicket: return TicketType.Rare;
        case BonusType.EpicTicket: return TicketType.Epic;
        case BonusType.LegendaryTicket: return TicketType.Legendary;
    }

    return;
}

export const ModalBonus = () => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [activeBonus, setActiveBonus] = useState<IUserBonus | null>(null);

    const { bonuses, loading, error } = useSelector(store => store.user.info);
    const currentContext = useSelector(store => store.appData.context);

    const dispatch = useDispatch();

    useEffect(() => {
        if (!bonuses || !currentContext)
            return;

        if (bonuses.length) {
            setIsOpen(true);
            setActiveBonus(bonuses[0]);
        }
    }, [bonuses, currentContext]);
    
    const handler = async () => {
        if (!bonuses.length || !activeBonus)
            return;

        const resultAction = await dispatch(applyBonuses(activeBonus.reason));
        if (applyBonuses.fulfilled.match(resultAction)) {
            if (activeBonus.reason === BonusReason.Welcome) {
                reachYMGoal(YMGoals.GetWelcomeBonus);
            }

            const { balances, fragments } = resultAction?.payload;

            if (balances !== undefined) {
                const tickets = omit(balances, 'balance');
                dispatch(setUserTickets(tickets));
                balances.balance && dispatch(setUserBalance({ balance: balances.balance }));
            }

            if (fragments !== undefined)
                dispatch(setFragments(fragments));
        }
    }

    if (!activeBonus)
        return <></>;

    const getBonusItems = () => {
        return activeBonus.bonuses.map((bonus, index) => {
            if (activeBonus.reason === BonusReason.Welcome && bonus.type === BonusType.Fragment)
                return null;

            switch (bonus.type) {
                case BonusType.Balance:
                    return (
                        <div key={`bonus${index}`} className={styles.item}>
                            <span className={styles.currency}>
                                <img src={currencyIcon} alt="Currency" />
                                { formatNumberWithSpaces(bonus.value) }
                            </span>
                        </div>
                    )

                case BonusType.RegularTicket:
                case BonusType.RareTicket:
                case BonusType.EpicTicket:
                case BonusType.LegendaryTicket: {
                    const type = convertBonusToTicketType(bonus.type);
                    return type
                        ? <div className={styles.item}>
                            <Ticket type={ type } value={ bonus.value } context='bonus' />
                        </div>
                        : null
                }

                case BonusType.Fragment:
                    return (
                        <div key={`bonus${index}`} className={styles.item}>
                            <span className={styles.currency}>
                                { `${bonus.value} ${trueFragmentsForm(bonus.value)} сертификата Wildberries` }
                            </span>
                        </div>
                    )

                default: return <></>
            }
        }).filter(item => item);
    }

    const getMessage = (reason: BonusReason) => {
        switch (reason) {
            case BonusReason.Welcome: return 'Вам начислен стартовый капитал.';
            case BonusReason.CollectionComplete: return 'Начислена награда за сбор коллекции.';
            case BonusReason.Compensation: return 'Вам начислена компенсация.';
            case BonusReason.Regular: return 'Вам начислен бонус.';
        }
    }

    const getModal = () => {
        return (
            <Modal
                name='ModalBonus'
                title='Поздравляем!'
                message={
                    <>
                        <p>{ getMessage(activeBonus.reason) }</p>
                        <div className={ styles.itemsContainer }>{ getBonusItems() }</div>
                    </>
                }

                button={ <ThinButton caption='Получить' handler={ handler } /> }
                icon={ bonusIcon }
            />
        );
    }

    return (
        <>
            { isOpen && bonuses && bonuses.length > 0 && (
                activeBonus.reason === BonusReason.Welcome
                    ? <CustomNotifyRouter closeHandler={ handler } customType={ CustomNotifyType.Welcome } data={ getBonusItems() } />
                    : getModal()
            )}

            { loading && <Loader /> }
            { error.length > 0 && <ErrorSnackbar errorMessage={ error } /> }
        </>
    );
}